import React, { useState, useMemo } from 'react';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollText, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

export default function OrgAuditLog() {
  const { org } = useAuth();
  const [actionFilter, setActionFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: profiles = [] } = useQuery({
    queryKey: ['org-audit-profiles', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data } = await supabase.from('profiles').select('id, full_name, email').eq('org_id', org.id);
      return data ?? [];
    },
    enabled: !!org,
  });

  const { data: auditLog = [], isLoading } = useQuery({
    queryKey: ['org-audit', org?.id],
    queryFn: async () => {
      if (!org) return [];
      const { data, error } = await supabase.from('audit_log').select('*').eq('org_id', org.id).order('created_at', { ascending: false }).limit(100);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!org,
  });

  const profileMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of profiles) m.set(p.id, p.full_name || p.email || p.id.slice(0, 8));
    return m;
  }, [profiles]);

  const actions = useMemo(() => [...new Set(auditLog.map(l => l.action))].sort(), [auditLog]);

  const filtered = useMemo(() => {
    let result = auditLog;
    if (actionFilter && actionFilter !== 'all') result = result.filter(l => l.action === actionFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(l => {
        const name = profileMap.get(l.actor_id) || '';
        return name.toLowerCase().includes(q);
      });
    }
    if (dateFilter !== 'all') {
      const now = Date.now();
      const ms = { '24h': 86400000, '7d': 604800000, '30d': 2592000000 }[dateFilter] ?? 0;
      if (ms) result = result.filter(l => now - new Date(l.created_at).getTime() < ms);
    }
    return result;
  }, [auditLog, actionFilter, searchQuery, dateFilter, profileMap]);

  if (!org) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">No account found.</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Audit Log</CardTitle>
        <CardDescription>Recent activity in {org.name}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by actor…" className="pl-9 h-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {actions.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={setDateFilter}>
            <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <ScrollText className="h-10 w-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm">No audit events found.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(log => (
                <TableRow key={log.id}>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell className="text-sm">{(log.actor_id && profileMap.get(log.actor_id)) ?? log.actor_id?.slice(0, 8) ?? 'system'}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs font-mono">{log.action}</Badge></TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {log.target_type ? `${log.target_type}:${log.target_id?.slice(0, 8)}` : '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-48 truncate">
                    {log.details && Object.keys(log.details as object).length > 0 ? JSON.stringify(log.details) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
